import axios, { AxiosError, type AxiosInstance } from 'axios';

const DEFAULT_TOKEN_KEY = 'solara.auth.token';

/**
 * The two SPAs share this package but must NOT share their JWT — the admin
 * console and the customer portal log in with different users. App shells set
 * `window.__SOLARA_TOKEN_KEY__` before mount to claim a private localStorage
 * slot ("solara.admin.token" / "solara.customer.token").
 */
function tokenKey(): string {
  if (typeof window === 'undefined') return DEFAULT_TOKEN_KEY;
  const fromGlobal = (window as unknown as { __SOLARA_TOKEN_KEY__?: string }).__SOLARA_TOKEN_KEY__;
  return fromGlobal ?? DEFAULT_TOKEN_KEY;
}

/**
 * Resolve the Solara Core base URL at runtime.
 *
 * Order:
 *   1. `window.__SOLARA_API_BASE__` (set by app shells before mount)
 *   2. `VITE_SOLARA_API_BASE` (env at build time)
 *   3. Convention: `/api` on the current origin (Traefik proxies it in prod)
 */
export function resolveApiBase(): string {
  if (typeof window !== 'undefined') {
    const fromGlobal = (window as unknown as { __SOLARA_API_BASE__?: string }).__SOLARA_API_BASE__;
    if (fromGlobal) return fromGlobal.replace(/\/$/, '');
  }
  const fromEnv =
    typeof import.meta !== 'undefined' && (import.meta as any).env?.VITE_SOLARA_API_BASE;
  if (typeof fromEnv === 'string' && fromEnv) return fromEnv.replace(/\/$/, '');
  if (typeof window !== 'undefined') return `${window.location.origin}/api`;
  return 'http://localhost:4001/api';
}

export function readToken(): string | null {
  if (typeof window === 'undefined') return null;
  const key = tokenKey();
  // Discard any legacy shared token from before the namespace split. We can't
  // tell which app it belonged to, and copying an admin token into the customer
  // portal slot triggers permanent 403s on tenant-scoped endpoints.
  if (key !== DEFAULT_TOKEN_KEY && window.localStorage.getItem(DEFAULT_TOKEN_KEY)) {
    window.localStorage.removeItem(DEFAULT_TOKEN_KEY);
  }
  return window.localStorage.getItem(key);
}

export function writeToken(token: string | null): void {
  if (typeof window === 'undefined') return;
  const key = tokenKey();
  if (token) window.localStorage.setItem(key, token);
  else window.localStorage.removeItem(key);
}

export interface ApiErrorShape {
  status: number;
  code?: string;
  message: string;
  /** Server-supplied details (validation errors, etc). */
  details?: unknown;
}

export class ApiError extends Error implements ApiErrorShape {
  status: number;
  code?: string;
  details?: unknown;

  constructor(input: ApiErrorShape) {
    super(input.message);
    this.name = 'ApiError';
    this.status = input.status;
    this.code = input.code;
    this.details = input.details;
  }
}

/** Translate an axios error into ApiError. */
function fromAxiosError(err: AxiosError): ApiError {
  const status = err.response?.status ?? 0;
  const data = err.response?.data as
    | { message?: string; error?: string; code?: string; statusCode?: number }
    | undefined;
  const message =
    (data && (data.message ?? data.error)) ?? err.message ?? 'Unknown API error';
  return new ApiError({
    status,
    code: data?.code,
    message: Array.isArray(message) ? message.join('; ') : String(message),
    details: data,
  });
}

let __client: AxiosInstance | undefined;

export function http(): AxiosInstance {
  if (__client) return __client;
  const client = axios.create({
    baseURL: resolveApiBase(),
    headers: { 'Content-Type': 'application/json' },
    // 30 s read timeout — long enough for the Peppol pipeline to wake up.
    timeout: 30_000,
  });
  client.interceptors.request.use((config) => {
    const token = readToken();
    if (token) {
      config.headers = config.headers ?? {};
      (config.headers as Record<string, string>).Authorization = `Bearer ${token}`;
    }
    return config;
  });
  client.interceptors.response.use(
    (res) => res,
    (err: AxiosError) => {
      // Only clear the token if the *identity* endpoint says we're not
      // authenticated. A 401 on a regular business endpoint just means the
      // caller lacks permission for that resource — we must not log them out.
      if (err.response?.status === 401) {
        const url = err.config?.url ?? '';
        if (/\/auth\/me$/.test(url)) writeToken(null);
      }
      return Promise.reject(fromAxiosError(err));
    },
  );
  __client = client;
  return client;
}

/** Test-only — reset the cached client (e.g. after changing the base URL). */
export function __resetHttpForTests(): void {
  __client = undefined;
}
